import React, { useState, useEffect } from 'react';
import Popover from '@material-ui/core/Popover';
import {
  InputLabel,
  Button,
  Grid,
  makeStyles,
  Divider,
  Box,
  FormControl
} from '@material-ui/core';
import { AutocompleteArrayInput, useInput, useListContext } from 'react-admin';
import FilterList from '@material-ui/icons/FilterList';
import { useStyles as useStylesTag } from '../TagsField';

const useStyles = makeStyles(({ palette, typography }) => ({
  buttonInputLike: {
    border: '1px solid #E0E0E0',
    backgroundColor: '#FFF',
    display: 'flex',
    width: '100%',
    top: '15.5%',
    alignItems: 'center',
    justifyContent: 'flex-start',
    height: '32px',
    fontFamily: typography.fontFamily,
    fontSize: typography.fontSize,
    fontWeight: '400',
    padding: '5px 5px 6px',
    color: '#757575',
    transition: 'none',
    '&:hover': {
      backgroundColor: 'unset',
      borderBottomColor: '#000'
    },
    '& > span > p': { opacity: '50%' },
    '& > span > span': { marginLeft: 'auto' }
  },
  buttonInputLikeForList: {
    marginBottom: '4px', // compensate the text helper that is present on a regular input
    marginTop: '16px', // when there is a label on top of the button
    minWidth: '172px'
  },
  popover: {
    width: '360px',
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between'
  },
  label: { marginBottom: '5px' },
  customButton: {
    backgroundColor: '#2962FF',
    color: '#ffffff',
    '&:hover': { backgroundColor: 'rgb(28, 68, 178)' }
  },
  box: {
    display: 'flex',
    justifyContent: 'space-between'
  },
  open: { color: '#2962FF' },
  button: { padding: '0px 15px' },
  buttonPrimary: { color: palette.primary.main },
  divider: { marginBlock: '14px' },
  singleTagInput: { marginLeft: 'unset !important' }
}));

const SelectTagArray = props => {
  const classes = useStyles();
  const {
    id,
    source,
    choices,
    selectedTags,
    onChange,
    selectLabel = ''
  } = props;
  const [inputLabel, setInputLabel] = useState('Select');

  function getSelectLabel() {
    if (selectLabel === '') {
      if (source === 'investortags') return 'Investor Tags';
      if (source === 'fundtags') return 'Fund Tags';
      if (source === 'fundInvestorTagId') return 'Fund-Investor Tags';
      return 'Fund-Investor Relationship Tags';
    } else {
      return selectLabel;
    }
  }

  const { input, isRequired, meta } = useInput({
    id,
    onChange,
    resource: source,
    source
  });

  input.value = selectedTags;

  useEffect(() => {
    selectedTags.length > 0 || meta.active
      ? setInputLabel('')
      : setInputLabel('Select');
  }, [selectedTags, meta]);

  return (
    <Grid item xs={12}>
      <InputLabel className={classes.label} htmlFor={id}>
        {getSelectLabel()}
      </InputLabel>
      <AutocompleteArrayInput
        id={id}
        label={inputLabel}
        source={source}
        choices={choices.sort((a, b) => a.name.localeCompare(b.name))}
        input={input}
        isRequired={isRequired}
        meta={meta}
      />
    </Grid>
  );
};

const TagFilter = props => {
  const classes = useStyles();
  const tagClasses = useStylesTag();
  const {
    source,
    onChange,
    tagsList,
    selectedTags,
    onChangeFundInv,
    fundInvTagsList,
    fundInvSelectedTags,
    displayTagFilter,
    displayFundInvTagFilter,
    isListFilter = false,
    selectLabel = '',
    topLabel = '',
    filterValues
  } = props;
  const [anchorEl, setAnchorEl] = useState(null);
  const handleClick = e => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const open = !!anchorEl;
  const id = open ? 'tag-popover' : undefined;

  const listContext = useListContext();

  const resetField = () => {
    // Clearing the TagFilter in a <List> component
    isListFilter &&
      listContext.setFilters({
        ...filterValues,
        [source]: []
      });
    onChange([]);
    onChangeFundInv([]);
  };

  const renderTag = () => {
    let tagItem;
    if (source !== 'fundInvestorTagId') {
      tagItem = tagsList.find(tag => tag.id === selectedTags[0]);
    } else {
      tagItem = fundInvTagsList.find(tag => tag.id === fundInvSelectedTags[0]);
    }
    return (
      <span className={`${tagClasses.tag} ${classes.singleTagInput}`}>
        {tagItem.name}
      </span>
    );
  };

  const getPlaceholder = () => {
    const selectedTagsCount =
      source !== 'fundInvestorTagId'
        ? selectedTags.length
        : fundInvSelectedTags.length;
    if (isListFilter) {
      if (selectedTagsCount === 0) return null;
      else if (selectedTagsCount === 1) return renderTag();
      else
        return (
          <span className={classes.singleTagInput}>
            {selectedTagsCount} tags selected
          </span>
        );
    } else {
      return <span className={classes.singleTagInput}>Tags</span>;
    }
  };

  return (
    <>
      <FormControl margin="dense">
        {isListFilter && <InputLabel>{topLabel}</InputLabel>}
        <Button
          className={`${classes.buttonInputLike} ${
            isListFilter ? classes.buttonInputLikeForList : ''
          }`}
          onClick={handleClick}
          aria-describedby={id}
          data-testid={'tags'}
          id={'tags'}
          endIcon={
            <FilterList
              className={selectedTags.length > 0 ? classes.open : ''}
            />
          }
        >
          {getPlaceholder()}
        </Button>
      </FormControl>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center'
        }}
      >
        <Grid className={classes.popover}>
          {displayTagFilter && (
            <SelectTagArray
              id="autoCompleteArrayInput"
              source={source}
              choices={tagsList}
              selectedTags={selectedTags}
              onChange={onChange}
              selectLabel={selectLabel}
            />
          )}
          {displayFundInvTagFilter && (
            <SelectTagArray
              id="autoCompleteArrayInputFI"
              source={
                source === 'fundInvestorTagId' ? source : 'fundinvestortags'
              }
              choices={fundInvTagsList}
              selectedTags={fundInvSelectedTags}
              onChange={onChangeFundInv}
            />
          )}
          <Grid item xs={12}>
            <Divider light={true} className={classes.divider} />
            <Box className={classes.box}>
              <Button
                onClick={resetField}
                className={`${classes.button} ${classes.buttonPrimary}`}
                variant={'outlined'}
              >
                Reset
              </Button>
              <Button
                onClick={handleClose}
                className={`${classes.button} ${classes.customButton}`}
              >
                Close
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Popover>
    </>
  );
};

export default TagFilter;
